import React, { useState } from "react";
import { Formik, Form, useField } from "formik";
import { loginSchema } from "../../Schema";
import { Box, Button, Callout, Flex, Text, Checkbox } from "@radix-ui/themes";
import { FaInfoCircle, FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

const LoginForm = ({ onForgotPassword }) => {
  const [loginError, setLoginError] = useState(null);
  const { login } = useAuth();

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setLoginError(null);
    try {
      await login(values.username, values.institutionId, values.password);
    } catch (error) {
      const errorMessage = typeof error === 'string'
        ? error
        : error.message || 'Login failed';
      setLoginError(errorMessage);
      setSubmitting(false);
    }
  };

  const InputField = ({ label, labelClassName, ...props }) => {
    const [field, meta] = useField(props);
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordField = props.type === "password";

    return (
      <div className="mb-4">
        <label className={labelClassName}>{label}</label>
        <div className="relative">
          <input
            {...field}
            {...props}
            type={isPasswordField && showPassword ? "text" : props.type}
            className={`mt-1 block w-full px-4 py-3 border rounded-md shadow-sm ${meta.touched && meta.error
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-blue-500"
              } ${isPasswordField ? "pr-10" : ""}`}
          />
          {isPasswordField && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none mt-1"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
            </button>
          )}
        </div>
        {meta.touched && meta.error && (
          <div className="mt-2 text-sm text-red-500">{meta.error}</div>
        )}
      </div>
    );
  };

  return (
    <Formik
      initialValues={{
        username: "",
        institutionId: "",
        password: "",
      }}
      validationSchema={loginSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          {/* Username Field */}
          <InputField
            label="Username or Email"
            name="username"
            type="text"
            placeholder="Enter your username or email"
            labelClassName="block text-sm font-medium text-gray-700"
          />

          {/* Institution ID Field */}
          <InputField
            label="Institution ID"
            name="institutionId"
            type="text"
            placeholder="Enter your Institution ID"
            labelClassName="block text-sm font-medium text-gray-700"
          />

          {/* Password Field */}
          <InputField
            label="Password"
            name="password"
            type="password"
            placeholder="Enter your password"
            labelClassName="block text-sm font-medium text-gray-700"
          />

          {/* Remember Me and Forgot Password */}
          <Flex justify="between" align="center" className="mb-4">
            <Text as="label" size="2">
              <Flex gap="2">
                <Checkbox color="indigo" defaultChecked />
                Remember me
              </Flex>
            </Text>
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm text-[#0b5a97] hover:text-blue-700 hover:underline"
            >
              Forgot password?
            </button>
          </Flex>

          {/* Login Error Message */}
          {loginError && (
            <div className="mb-4 text-red-500 flex items-center">
              <FaInfoCircle className="mr-2" />
              <span>{loginError}</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="my-4">
            <button
              type="submit"
              className="w-full py-3 px-4 bg-[#0b5a97] text-white font-medium rounded-sm shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Logging in..." : "Login"}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;